import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KidzkulamLogo } from "@/components/Logo";
import { Loader2 } from "lucide-react";

// Google "G" mark
function GoogleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.4 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41.9 34.6 44 29.7 44 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

export default function Login() {
  const { login, error } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) nav("/");
  };

  const demoLogin = (em, pw) => {
    setEmail(em);
    setPassword(pw);
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 bg-background">
      {/* Left visual */}
      <div className="relative hidden md:flex flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 bg-confetti opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/40 to-white/70" />
        <div className="relative z-10">
          <KidzkulamLogo size={54} />
        </div>
        <div className="relative z-10 space-y-4">
          <h1 className="text-5xl font-black leading-tight text-[hsl(142_60%_22%)]">
            Let's Make Future,<br />one little smile at a time.
          </h1>
          <p className="text-lg font-medium text-foreground/70 max-w-md">
            The everyday companion for Kidzkulam — attendance, fees, enquiries and daily
            classroom moments, all in one warm little home.
          </p>
        </div>
        <div className="relative z-10 flex gap-2 flex-wrap">
          <div className="chip-green px-4 py-1.5 rounded-full text-sm font-bold">Attendance in one tap</div>
          <div className="chip-pink px-4 py-1.5 rounded-full text-sm font-bold">Fee reminders</div>
          <div className="chip-teal px-4 py-1.5 rounded-full text-sm font-bold">Photo diary</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex flex-col justify-center items-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8 flex justify-center">
            <KidzkulamLogo size={44} />
          </div>
          <h2 className="text-3xl font-extrabold mb-1">Welcome back</h2>
          <p className="text-muted-foreground mb-8 font-medium">Sign in to continue to your school.</p>

          <button
            type="button"
            data-testid="google-signin"
            onClick={startGoogle}
            className="w-full h-12 rounded-full border-2 border-border bg-white flex items-center justify-center gap-3 font-bold text-foreground hover:bg-muted hover:-translate-y-0.5 hover:shadow-md transition-all mb-5"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-bold">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                placeholder="you@kidzkulam.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-2xl"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-bold">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-2xl"
                required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm font-semibold text-destructive bg-destructive/10 px-4 py-3 rounded-xl">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-12 rounded-full text-base font-bold shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-border">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
              Try a demo account
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["Admin", "mayank8987@gmail.com", "Kidzkulam@2026"],
                ["Teacher", "teacher.priya@kidzkulam.local", "Teacher@123"],
                ["Front Desk", "frontdesk@kidzkulam.local", "Frontdesk@123"],
                ["Parent", "parent.aarav@kidzkulam.local", "Parent@123"],
              ].map(([label, em, pw]) => (
                <button
                  key={label}
                  type="button"
                  data-testid={`demo-${label.toLowerCase().replace(" ", "-")}`}
                  onClick={() => demoLogin(em, pw)}
                  className="px-3 py-2 rounded-xl text-sm font-semibold bg-muted hover:bg-primary/10 transition-colors text-left"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
